import * as React from "react";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useCartContext} from "../cart/CartContextProvider/CartContextProvider";
import Breadcrumb from "./Breadcrumb";
import "./Checkout.scss";
import {useCheckoutViewContext} from "./CheckoutContextProvider/CheckoutViewContextProvider";
import AddressStep from "./steps/AddressStep/AddressStep";
import Finished from "./steps/Finished/Finished";
import Login from "./steps/Login/Login";
import Payment from "./steps/Payment/Payment";
import Shipping from "./steps/Shipping/Shipping";
import Summary from "./steps/Summary/Summary";
import {IProductTrackingData, IPurchaseSummaryInformation, Tracking} from "../../tracking/tracking";
import {CartPosition} from "@connox/typescript-library";
import {useCheckoutContext} from "./CheckoutContextProvider/CheckoutContextProvider";

const Checkout: React.FC = () => {
  const {t} = useTranslation("checkout");
  const {step} = useCheckoutViewContext();
  const {checkout} = useCheckoutContext();
  const {cart} = useCartContext();

  useEffect(() => {
    const productData: IProductTrackingData[] = [];

    cart.positions.forEach((position: CartPosition) => {
      if (position.type === "Item" && position.item?.itemId) {
        productData.push({
          brand: position.item.manufacturer,
          id: position.item.itemId,
          name: position.text,
          price: position.price.amount,
          quantity: position.quantity,
        });
      }
    });

    if (step !== CheckoutStep.FINISHED) {
      Tracking.sendCheckoutStepEvent(step, productData);
    } else if (step === CheckoutStep.FINISHED && checkout.orderId) {
      const shippingCosts = cart.positions.find((pos) => {
        return pos.type === "Shipping" ? pos : undefined;
      })?.amount.amount;

      const voucherCode = cart.positions.find((pos) => {
        return pos.type === "Voucher" ? pos : undefined;
      })?.text;

      const promotionId = cart.promotion?.id ? cart.promotion.id : -1;

      const summaryData: IPurchaseSummaryInformation = {
        id: checkout.orderId,
        affiliation: promotionId,
        coupon: voucherCode ? voucherCode : "",
        revenue: cart.amountGross.amount,
        shipping: shippingCosts ? shippingCosts : "0",
        tax: cart.taxAmounts[0].amount.amount,
      };

      Tracking.sendPurchaseEvent(summaryData, productData);
    }
  }, [step]);

  const showBreadCrumbs = step !== CheckoutStep.FINISHED && step !== CheckoutStep.CARTERRORS;

  return (
    <>
      {showBreadCrumbs && <Breadcrumb />}
      {step === CheckoutStep.CARTERRORS && (
        <CheckoutError>{t("Es befinden sich keine oder fehlerhafte Artikel in Ihrem Warenkorb!")}</CheckoutError>
      )}
      <Login isVisible={step === CheckoutStep.LOGIN} />
      <AddressStep isVisible={step === CheckoutStep.ADDRESS} />
      <Shipping isVisible={step === CheckoutStep.SHIPPING} />
      <Payment isVisible={step === CheckoutStep.PAYMENT} />
      {step === CheckoutStep.SUMMARY && <Summary isVisible={step === CheckoutStep.SUMMARY} />}
      <Finished isVisible={step === CheckoutStep.FINISHED} />
    </>
  );
};
export default Checkout;
export const CheckoutError: React.FC = (props) => {
  const {t} = useTranslation("cart");
  const listOfErrors = React.Children.map(props.children, (child: string) => {
    return !child ? undefined : <li>{child}</li>;
  });
  if (!(listOfErrors && listOfErrors.length)) {
    return null;
  }

  return (
    <div className="reduced-width">
      <div className="error-block-main">
        <strong>{t("Wir haben einen Fehler entdeckt!", {count: listOfErrors.length, ns: "cart"})}</strong>
        <ul>{listOfErrors}</ul>
      </div>
    </div>
  );
};
export enum CheckoutStep {
  CARTERRORS = "carterrors",
  LOGIN = "login",
  ADDRESS = "address",
  SHIPPING = "shipping",
  PAYMENT = "payment",
  SUMMARY = "summary",
  FINISHED = "finished",
}
export enum LoginMode {
  NEWCUSTOMER = "new",
  KNOWNCUSTOMER = "known",
  FACEBOOK = "facebook",
}
export enum PaymentMethod {
  PAYPAL = "PayPal",
  AMAZONPAY = "AmazonPay",
  GOOGLEPAY = "GooglePay",
  APPLEPAY = "ApplePay",
}
export interface IIsVisible {
  isVisible: boolean;
}

export const DEFAULT_ERROR_MESSAGE = "default error message";
